import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Loader2 } from "lucide-react"
import { FavoriteGroup } from "@/types/networking"
import {
  getFavoriteGroups,
  createFavoriteGroup,
  addPersonToFavoriteGroup,
  removePersonFromFavoriteGroup,
} from "../services/favoritesService"
import { useToast } from "@/contexts/ToastContext"

interface AddToFavoritesDialogProps {
  memberUid: string
  isOpen: boolean
  onClose: () => void
  onFavoritesChange: () => void
}

export function AddToFavoritesDialog({
  memberUid,
  isOpen,
  onClose,
  onFavoritesChange,
}: AddToFavoritesDialogProps) {
  const [groups, setGroups] = useState<FavoriteGroup[]>([])
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const [initialGroupId, setInitialGroupId] = useState<number | null>(null)
  const [newGroupName, setNewGroupName] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (isOpen) {
      const fetchGroups = async () => {
        try {
          setLoading(true)
          const favoriteGroups = await getFavoriteGroups()
          setGroups(favoriteGroups)
          const currentGroup = favoriteGroups.find((g) =>
            g.pessoas.some((p) => p.uid === memberUid)
          )
          const currentId = currentGroup ? currentGroup.appfav_id : null
          setSelectedGroupId(currentId)
          setInitialGroupId(currentId)
        } catch (error) {
          console.error("Failed to fetch favorite groups", error)
        } finally {
          setLoading(false)
        }
      }
      fetchGroups()
    } else {
      setNewGroupName("")
      setSelectedGroupId(null)
      setInitialGroupId(null)
    }
  }, [isOpen, memberUid])

  const handleToggleGroup = (groupId: number) => {
    setSelectedGroupId((prev) => (prev === groupId ? null : groupId))
    setNewGroupName("")
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      let groupIdToSave = selectedGroupId

      if (newGroupName.trim()) {
        const newGroup = await createFavoriteGroup({
          appfav_descricao: newGroupName.trim(),
        })
        groupIdToSave = newGroup.appfav_id
      }

      // Remove from current group if was in one
      if (initialGroupId && initialGroupId !== groupIdToSave) {
        await removePersonFromFavoriteGroup(initialGroupId, memberUid)
      }

      if (!groupIdToSave) {
        // All deselected — remove from favorites
        if (initialGroupId) {
          toast.success("Membro removido dos favoritos.", "Sucesso!")
          onFavoritesChange()
        }
        onClose()
        return
      }

      // Add to new group (only if changed or new)
      if (groupIdToSave !== initialGroupId) {
        await addPersonToFavoriteGroup(groupIdToSave, memberUid)
      }

      toast.success("Membro adicionado aos favoritos.", "Sucesso!")

      onFavoritesChange()
      onClose()
    } catch (error) {
      toast.danger("Ocorreu um erro ao salvar os favoritos.", "Erro!")
      console.error("Failed to save favorites", error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar aos Favoritos</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Selecione um grupo para adicionar este membro ou crie um novo.
            </p>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {groups.map((group) => {
                const isChecked = selectedGroupId === group.appfav_id
                return (
                  <label
                    key={group.appfav_id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
                      isChecked
                        ? "border-orange-500 bg-orange-500/10"
                        : "border-zinc-700 hover:border-zinc-600"
                    }`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => handleToggleGroup(group.appfav_id)}
                    />
                    <span className="text-sm font-medium leading-none">
                      {group.appfav_descricao}
                    </span>
                    <span className="ml-auto text-xs text-zinc-500">
                      {group.pessoas.length} {group.pessoas.length === 1 ? "membro" : "membros"}
                    </span>
                  </label>
                )
              })}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="new-group"
                className="text-sm font-medium"
              >
                Ou crie um novo grupo
              </label>
              <Input
                id="new-group"
                placeholder="Ex: Colegas de mentoria"
                value={newGroupName}
                onChange={(e) => {
                  setNewGroupName(e.target.value)
                  if (e.target.value.trim()) setSelectedGroupId(null)
                }}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
